import React, { createContext, useContext, useEffect, useState } from "react";
import { api, imgUrl, IS_PARTNER_DOMAIN, SHOP_CODE } from "@/lib/api";
import { setShopContacts } from "@/lib/site";

const ShopCtx = createContext(null);
export const useShop = () => useContext(ShopCtx);
export const SHOP_EXPECTED = IS_PARTNER_DOMAIN || !!SHOP_CODE;

const BRAND_RX = /Corsando(\.net)?/g;
const PANEL_RX = /^\/(partner\/|amministrazione|operatori)/;

function rebrand(name, price) {
  const PRICE_RX = /11,99\s?€/g;
  const swap = (t) => { let o = t.replace(BRAND_RX, name); if (price) o = o.replace(PRICE_RX, `${price} €`); return o; };
  const fix = (node) => {
    if (node.nodeType === 3) { const v = swap(node.nodeValue); if (v !== node.nodeValue) node.nodeValue = v; return; }
    if (node.nodeType !== 1 || ["SCRIPT", "STYLE", "TEXTAREA", "INPUT"].includes(node.tagName)) return;
    if (node.tagName === "IMG" && node.getAttribute("alt")?.includes("Corsando")) node.setAttribute("alt", name);
    node.childNodes.forEach(fix);
  };
  const run = () => {
    if (PANEL_RX.test(window.location.pathname)) return;
    fix(document.body);
    const tt = swap(document.title); if (tt !== document.title) document.title = tt;
  };
  run();
  const mo = new MutationObserver(run);
  mo.observe(document.body, { childList: true, subtree: true, characterData: true });
  const t = new MutationObserver(run);
  const titleEl = document.querySelector("title");
  if (titleEl) t.observe(titleEl, { childList: true });
  return () => { mo.disconnect(); t.disconnect(); };
}

const NotLive = ({ shop }) => (
  <div className="min-h-screen grid place-items-center bg-[#0b0b10] text-white p-6 text-center" data-testid="shop-not-live">
    <div>
      {shop.logo ? <img src={imgUrl(shop.logo)} alt={shop.name} className="h-12 mx-auto mb-6" /> : <h1 className="text-3xl font-extrabold mb-4">{shop.name}</h1>}
      <p className="text-slate-300">Stiamo preparando il negozio. Torna a trovarci presto!</p>
    </div>
  </div>
);

export function ShopProvider({ children }) {
  const [shop, setShop] = useState(SHOP_EXPECTED ? undefined : null);
  useEffect(() => {
    if (!SHOP_EXPECTED) return;
    api.get("/shop/config").then(({ data }) => { if (data.shop) setShopContacts(data.shop.contacts); setShop(data.shop || null); }).catch(() => setShop(null));
  }, []);
  useEffect(() => {
    if (!shop) return;
    const root = document.documentElement.style;
    const c = shop.colors || {};
    if (c.primary) { root.setProperty("--cs-red", c.primary); root.setProperty("--cs-red-hover", c.accent || c.primary); }
    const vars = { bg: "--cs-bg", bg2: "--cs-bg2", card: "--cs-card", border: "--cs-border", gold: "--cs-gold", text: "--cs-text" };
    Object.entries(vars).forEach(([k, v]) => c[k] && root.setProperty(v, c[k]));
    if (c.text) document.body.classList.add("shop-text");
    if (shop.font && shop.font !== "Default") {
      const l = document.createElement("link");
      l.rel = "stylesheet"; l.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(shop.font)}:wght@400;600;700;800&display=swap`;
      document.head.appendChild(l);
      root.setProperty("--shop-font", `"${shop.font}", sans-serif`);
      document.body.classList.add("shop-font");
    }
    const fav = document.querySelector("link[rel='icon']");
    if (fav && shop.logo) fav.href = imgUrl(shop.logo);
    return rebrand(shop.name, shop.prices?.monthly ? Number(shop.prices.monthly).toFixed(2).replace(".", ",") : null);
  }, [shop]);
  if (shop === undefined) return <div className="min-h-screen bg-[#0b0b10]" />;
  if (shop && !shop.live && !SHOP_CODE && !PANEL_RX.test(window.location.pathname)) return <NotLive shop={shop} />;
  return <ShopCtx.Provider value={shop}>{children}</ShopCtx.Provider>;
}

export const BrandLogo = ({ className, fallbackClass = "font-display font-extrabold text-white text-xl" }) => {
  const shop = useShop();
  if (!shop) return <img src="/corsando-logo.png" alt="Corsando" className={className} />;
  return shop.logo ? <img src={imgUrl(shop.logo)} alt={shop.name} className={className} /> : <span className={fallbackClass}>{shop.name}</span>;
};
